/*
  Copyright (c) Epic Games, Inc. All Rights Reserved.
  CONTAINS AI GENERATED CODE.
*/

import { CSSProperties } from "react";
import { cx } from "@panda/css";
import { HStack } from "@panda/jsx";
import { token } from "@panda/tokens";
import { CosmosLink, EdsIcon, EdsIconType, IconPosition } from "..";
import { CosmosEnumEdsGradients } from "@cosmos/hygraph-schema";
import { tag } from "./CosmosTag.style";

export type CosmosTagSizeEnum = "sm" | "md" | "lg" | "uiSM";

export interface CosmosTagCssVariables extends CSSProperties {
	"--tag-color"?: string;
	"--tag-background-color"?: string;
	"--tag-hover-background-color"?: string;
}

export interface CosmosTagProps {
	className?: string;
	label?: string | null;
	href?: string | null;
	size?: CosmosTagSizeEnum;
	icon?: EdsIconType;
	gradient?: CosmosEnumEdsGradients | null;
	accent?: CosmosEnumEdsGradients | null;
	iconPosition?: IconPosition;
	color?: string;
	backgroundColor?: string;
}

export function CosmosTag({
	className = "",
	label,
	href,
	size = "md",
	icon,
	accent,
	gradient,
	iconPosition = "start",
	color = "inherit",
	backgroundColor = "inherit",
}: CosmosTagProps): React.JSX.Element | null {
	if (!label && !icon) return null;
	if (!!accent || !!gradient) {
		color = token(`colors.eds.text.onAccent`);
		if (gradient) {
			backgroundColor = token(`gradients.eds.gradient.${gradient}`);
		} else if (accent) {
			backgroundColor = token(`colors.eds.palette.accent.${accent}200`);
		}
	}

	const styleVars: CosmosTagCssVariables = {
		"--tag-color": color,
		"--tag-background-color": backgroundColor,
		"--tag-hover-background-color": token("colors.eds.fill.secondary.hover"),
	};

	const classes = tag({ size });

	return (
		<CosmosLink
			href={href || ""}
			className={cx(className, "tag", classes.anchor, href ? "hoverable" : "")}
			disabled={!href}
			style={styleVars}
			analytics={{
				eventCategory: "hyperlink",
				eventLabel: label || "",
				eventValue: {
					component: "CosmosTag",
					href,
				},
			}}
			title={label ?? undefined}
		>
			<HStack
				gap={size === "lg" ? "eds.dimension.8" : "eds.dimension.6"}
				mdDown={{ gap: "eds.dimension.4" }}
				flexDirection={iconPosition === "start" ? "row-reverse" : "row"}
			>
				<p className={`label ${classes.label}`}>{label}</p>
				{icon ? <EdsIcon icon={icon} size="sm" /> : null}
			</HStack>
		</CosmosLink>
	);
}
